import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "./CodeFormat.css";
import API_URL from '../../api/ExamCalendarApi';

const ExamManager = () => {
  const [exams, setExams] = useState([]);
  const [newExamName, setNewExamName] = useState("");
  const [newCities, setNewCities] = useState([{ cityId: "", hallTicketFormat: "" }]);
  const [editingExam, setEditingExam] = useState(null);
  const [editedExamName, setEditedExamName] = useState("");
  const [editedCities, setEditedCities] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [centres, setCentres] = useState([]); // All states/districts/cities
  const [validCities, setValidCities] = useState([]); // Only cities with centres

  const formRef = useRef(null);
  const [focusedField, setFocusedField] = useState(null);

  // Fetch all centres and extract valid cities
  const fetchCentres = async () => {
    try {
      const response = await axios.get(`${API_URL}/centre/centres`);
      const allStates = response.data.states || [];
      const validCityList = [];

      allStates.forEach(state => {
        state.districts?.forEach(district => {
          district.cities?.forEach(city => {
            if (Array.isArray(city.centres) && city.centres.length > 0) {
              validCityList.push({
                _id: city._id,
                cityName: city.cityName.trim(),
              });
            }
          });
        });
      });

      // Optional: Remove duplicates by _id
      const uniqueCities = validCityList.filter(
        (city, index, self) => index === self.findIndex(c => c._id === city._id)
      );

      setValidCities(uniqueCities);
      setCentres(response.data.states);
    } catch (error) {
      console.error('Error fetching centres:', error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load city options.",
      });
    }
  };

  useEffect(() => {
    fetchExams();
    fetchCentres(); // Load city dropdown options
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isEditing) {
        setFocusedField('all');
        setTimeout(() => setFocusedField(null), 2000);
      }
    }, 100);
    return () => clearTimeout(timeout);
  }, [isEditing]);

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API_URL}/CodeFormat/get-all-examCodeData`);
      setExams(response.data);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error fetching exams. Please try again.",
      });
      console.error("Error fetching exams:", error);
    }
  };

  const addExam = async () => {
    if (!newExamName || newCities.some(city => !city.cityId || !city.hallTicketFormat)) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please fill in all fields",
      });
      return;
    }

    // Map cityId to cityName for backend (if needed)
    const citiesWithNames = newCities.map(c => {
      const cityObj = validCities.find(v => v._id === c.cityId);
      return {
        cityName: cityObj?.cityName || "",
        hallTicketFormat: c.hallTicketFormat,
      };
    });

    try {
      const response = await axios.post(`${API_URL}/CodeFormat/add-format`, {
        examName: newExamName.toUpperCase(),
        cities: citiesWithNames,
      });
      setExams([...exams, response.data]);
      setNewExamName("");
      setNewCities([{ cityId: "", hallTicketFormat: "" }]);
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Exam added successfully!",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error adding exam. Please try again.",
      });
      console.error("Error adding exam:", error);
    }
  };

  const handleCityChange = (index, field, value) => {
    const updated = [...newCities];
    updated[index][field] = value;
    setNewCities(updated);
  };

  const addCityField = () => {
    setNewCities([...newCities, { cityId: "", hallTicketFormat: "" }]);
  };

  const removeCityField = (index) => {
    setNewCities(newCities.filter((_, i) => i !== index));
  };

  const enableEdit = (exam) => {
    setEditingExam(exam._id);
    setEditedExamName(exam.examName);

    // Convert existing cities to use cityId for dropdown
    const mappedCities = exam.cities.map(city => {
      const match = validCities.find(v => v.cityName === city.cityName);
      return {
        cityId: match ? match._id : "",
        hallTicketFormat: city.hallTicketFormat,
        originalCityName: city.cityName, // fallback if not found
      };
    });

    setEditedCities(mappedCities);
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveExamChanges = async () => {
    if (!editedExamName || editedCities.some(city => !city.cityId || !city.hallTicketFormat)) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please fill in all fields",
      });
      return;
    }

    const citiesWithNames = editedCities.map(c => {
      const cityObj = validCities.find(v => v._id === c.cityId);
      return {
        cityName: cityObj?.cityName || c.originalCityName || "",
        hallTicketFormat: c.hallTicketFormat,
      };
    });

    try {
      await axios.put(`${API_URL}/CodeFormat/edit-exam`, {
        examId: editingExam,
        examName: editedExamName.toUpperCase(),
        cities: citiesWithNames,
      });
      fetchExams();
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Exam updated successfully!",
      });
      setEditingExam(null);
      setIsEditing(false);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error updating exam. Please try again.",
      });
      console.error("Error updating exam:", error);
    }
  };

  const cancelEdit = () => {
    setEditingExam(null);
    setIsEditing(false);
  };

  const deleteCity = async (examId, cityId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action will permanently remove the city from this exam.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_URL}/CodeFormat/exams/${examId}/cities/${cityId}`);
        fetchExams();
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "City has been deleted successfully.",
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "There was an error deleting the city. Please try again.",
        });
        console.error("Error deleting city:", error);
      }
    }
  };

  const deleteExam = async (examId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_URL}/CodeFormat/delete-exam/${examId}`);
        fetchExams();
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Exam deleted successfully!",
        });
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Error deleting exam. Please try again.",
        });
        console.error("Error deleting exam:", error);
      }
    }
  };

  // Helper: Get city name by ID (for display in edit mode if needed)
  const getCityNameById = (id) => {
    const city = validCities.find(c => c._id === id);
    return city ? city.cityName : "";
  };

  return (
    <div>
      <div className="admin-codeformat-container">
        <h2 className="admin-codeformat-title">CodeFormat for Centre for Each Exam</h2>

        {editingExam ? (
          <form className="admin-codeformat-form" ref={formRef}>
            <h4 className="admin-codeformat-subtitle">Edit Exam</h4>
            <div className="admin-codeformat-row">
              <div className="admin-codeformat-col-md-4">
                <input
                  type="text"
                  className={`admin-codeformat-form-control ${focusedField === "all" || focusedField === "examName" ? "sparkle" : ""}`}
                  placeholder="Exam Name"
                  value={editedExamName}
                  onChange={(e) => setEditedExamName(e.target.value)}
                />
              </div>
            </div>
            <h5 className="admin-codeformat-small-title">Edit Cities</h5>
            {editedCities.map((city, index) => (
              <div key={index} className="admin-codeformat-row1">
                <div className="admin-codeformat-col-md-5">
                  <select
                    className={`admin-codeformat-form-control ${focusedField === "all" || focusedField === `city-${index}-name` ? "sparkle" : ""}`}
                    value={city.cityId}
                    onChange={(e) =>
                      setEditedCities(
                        editedCities.map((c, i) =>
                          i === index ? { ...c, cityId: e.target.value } : c
                        )
                      )
                    }
                  >
                    <option value="">-- Select City --</option>
                    {validCities.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.cityName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-codeformat-col-md-5">
                  <input
                    type="text"
                    className={`admin-codeformat-form-control ${focusedField === "all" || focusedField === `city-${index}-hallTicketFormat` ? "sparkle" : ""}`}
                    placeholder="Hall Ticket Format"
                    value={city.hallTicketFormat}
                    onChange={(e) =>
                      setEditedCities(
                        editedCities.map((c, i) =>
                          i === index ? { ...c, hallTicketFormat: e.target.value } : c
                        )
                      )
                    }
                  />
                </div>
                <div className="admin-codeformat-col-md-2">
                  <button
                    type="button"
                    className="admin-codeformat-btn admin-codeformat-btn-danger"
                    onClick={() =>
                      setEditedCities(editedCities.filter((_, i) => i !== index))
                    }
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="admin-codeformat-btn admin-codeformat-btn-secondary"
              onClick={() => setEditedCities([...editedCities, { cityId: "", hallTicketFormat: "" }])}
            >
              + Add City
            </button>
            <br />
            <button
              type="button"
              className="admin-codeformat-btn admin-codeformat-btn-primary"
              onClick={saveExamChanges}
            >
              Save Changes
            </button>
            <button
              type="button"
              className="admin-codeformat-btn admin-codeformat-btn-secondary"
              onClick={cancelEdit}
            >
              Cancel
            </button>
          </form>
        ) : (
          <form className="admin-codeformat-form">
            <h4 className="admin-codeformat-subtitle">Add New Exam</h4>
            <div className="admin-codeformat-row">
              <div className="admin-codeformat-col-md-4">
                <input
                  type="text"
                  className={`admin-codeformat-form-control ${focusedField === "all" || focusedField === "newExamName" ? "sparkle" : ""}`}
                  placeholder="Exam Name"
                  value={newExamName}
                  onChange={(e) => setNewExamName(e.target.value)}
                />
              </div>
            </div>
            <h5 className="admin-codeformat-small-title">Add Cities</h5>
            {newCities.map((city, index) => (
              <div key={index} className="admin-codeformat-row1">
                <div className="admin-codeformat-col-md-5">
                  <select
                    className={`admin-codeformat-form-control ${focusedField === "all" || focusedField === `newCity-${index}-name` ? "sparkle" : ""}`}
                    value={city.cityId}
                    onChange={(e) => handleCityChange(index, "cityId", e.target.value)}
                  >
                    <option value="">-- Select City --</option>
                    {validCities.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.cityName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-codeformat-col-md-5">
                  <input
                    type="text"
                    className={`admin-codeformat-form-control ${focusedField === "all" || focusedField === `newCity-${index}-hallTicketFormat` ? "sparkle" : ""}`}
                    placeholder="Hall Ticket Format"
                    value={city.hallTicketFormat}
                    onChange={(e) => handleCityChange(index, "hallTicketFormat", e.target.value)}
                  />
                </div>
                <div className="admin-codeformat-col-md-2">
                  <button
                    type="button"
                    className="admin-codeformat-btn admin-codeformat-btn-danger"
                    onClick={() => removeCityField(index)}
                    disabled={newCities.length === 1}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="admin-codeformat-btn admin-codeformat-btn-secondary"
              onClick={addCityField}
            >
              + Add City
            </button>
            <br />
            <button
              type="button"
              className="admin-codeformat-btn admin-codeformat-btn-primary"
              onClick={addExam}
            >
              Add Exam
            </button>
          </form>
        )}

        <h4 className="admin-codeformat-subtitle">Existing Exams</h4>
        {exams.length === 0 ? (
          <p>No exams found</p>
        ) : (
          exams.map((exam) => (
            <div key={exam._id} className="admin-codeformat-card">
              <div className="admin-codeformat-card-body">
                <h5 className="admin-codeformat-card-title">
                  {exam.examName}
                  <button
                    type="button"
                    className="admin-codeformat-btn admin-codeformat-btn-primary"
                    onClick={() => enableEdit(exam)}
                  >
                    Edit
                  </button>
                </h5>
                <div className="admin-codeformat-table-responsive">
                  <table className="admin-codeformat-table">
                    <thead>
                      <tr>
                        <th>City</th>
                        <th>Hall Ticket Format</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exam.cities.map((city) => (
                        <tr key={city._id}>
                          <td>{city.cityName}</td>
                          <td>{city.hallTicketFormat}</td>
                          <td>
                            <button
                              className="admin-codeformat-btn admin-codeformat-btn-danger admin-codeformat-btn-sm"
                              onClick={() => deleteCity(exam._id, city._id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  className="admin-codeformat-btn admin-codeformat-btn-danger admin-codeformat-btn-block"
                  onClick={() => deleteExam(exam._id)}
                >
                  Delete Exam
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ExamManager;